import { useMemo } from 'react';
import { Button } from '@antimatterhq/ui/components/button';
import { Text } from '@antimatterhq/ui/components/typography';
import { Icon } from '@antimatterhq/ui/components/icon';
import { DatasourceUpgradeStringFieldDiff } from '@/lib/datasource/upgrade/datasourceUpgrade.types';

export const DatasourceUpgradeDiffFixedString = ({
  diff,
  onAccept,
  onReject,
  isAccepting,
  isRejecting,
}: {
  diff: DatasourceUpgradeStringFieldDiff;
  onAccept: () => void;
  onReject: () => void;
  isAccepting?: boolean;
  isRejecting?: boolean;
}) => {
  const currentValueKept = useMemo(
    () =>
      diff.status === 'confirmed' && diff.currentValue === diff.upgradeValue,
    [diff.currentValue, diff.status, diff.upgradeValue],
  );

  const upgradePresetValueApplied = useMemo(
    () =>
      diff.status === 'confirmed' &&
      diff.upgradePresetValue === diff.upgradeValue,
    [diff.status, diff.upgradePresetValue, diff.upgradeValue],
  );

  return (
    <div
      className="flex w-full flex-col"
      data-testid={`fieldspec-diff-fixedstring-root-${diff.title}`}
    >
      <div className="flex w-full">
        <div className="flex-1 text-center">
          <div className="flex items-center">
            <div className="flex-1"></div>
            <div data-testid="fieldspec-diff-fixedstring-current-value">
              <Text bold>
                {diff.currentValue ? (
                  `"${diff.currentValue}"`
                ) : (
                  <span className="italic text-gray-500">no value</span>
                )}
              </Text>
            </div>

            <div className="flex-1">
              {currentValueKept && (
                <Icon
                  icon="lucide:check"
                  className="ml-2 text-green-500"
                  data-testid="fieldspec-diff-fixedstring-rejected-icon"
                />
              )}
            </div>
          </div>
        </div>
        <div className="flex-1 text-center">
          <div className="flex items-center">
            <div className="flex-1"></div>
            <div data-testid="fieldspec-diff-fixedstring-upgrade-value">
              <Text bold>
                {diff.upgradePresetValue ? (
                  `"${diff.upgradePresetValue}"`
                ) : (
                  <span className="italic text-gray-500">No value</span>
                )}
              </Text>
            </div>
            <div className="flex-1">
              {upgradePresetValueApplied && (
                <Icon
                  icon="lucide:check"
                  className="ml-2 text-green-500"
                  data-testid="fieldspec-diff-fixedstring-accepted-icon"
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex w-full pt-4">
        <div className="flex-1 text-center">
          <Button
            variant="default"
            onClick={onReject}
            disabled={currentValueKept || isRejecting || isAccepting}
            data-testid="datasource-upgrade-diff-fixedstring-reject-btn"
          >
            {!upgradePresetValueApplied && 'Keep existing version'}
            {upgradePresetValueApplied && 'Revert to existing version'}
          </Button>
        </div>
        <div className="flex-1 text-center">
          <Button
            variant="primary"
            onClick={onAccept}
            disabled={upgradePresetValueApplied || isRejecting || isAccepting}
            data-testid="datasource-upgrade-diff-fixedstring-accept-btn"
          >
            Apply pending version
          </Button>
        </div>
      </div>
    </div>
  );
};
